// Ported from vlookup-dramatic/app/how-it-works/programme-timeline.tsx by scripts/revamp/port-design.js
import React, { useEffect, useRef, useState } from "react";
import { programme } from "../../lib/content";
import { cn } from "../../lib/utils";
// Labels read "Month 1 – Connect" / "Months 2–3 – Work": split on the spaced
// dash only (en or em) so the "2–3" range survives, then read how many months
// the phase covers.
const phases = programme.months.map((m) => {
    const [phase, title] = m.label.split(/\s+[–—]\s+/).map((s) => s.trim());
    const range = phase.match(/(\d+)\s*[–—-]\s*(\d+)/);
    return {
        label: m.label,
        desc: m.desc,
        phase,
        title,
        span: range ? Number(range[2]) - Number(range[1]) + 1 : 1,
    };
});
// How long each phase holds the floor before the walk-through moves on.
const STEP_MS = 2000;
function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
    useEffect(() => {
        const mq = window.matchMedia(query);
        const onChange = () => setMatches(mq.matches);
        onChange();
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, [query]);
    return matches;
}
// Desktop walk-through: equal months, then each phase widens in turn. Phone
// and tablet keep equal cards with no highlight — the old RESTING state left
// Months 2–3 stuck on, and a tablet touch could freeze the walk there.
export function ProgrammeTimeline() {
    const ref = useRef(null);
    const [step, setStep] = useState(0);
    const [inView, setInView] = useState(false);
    const [paused, setPaused] = useState(false);
    const [settled, setSettled] = useState(false);
    const wide = useMediaQuery("(min-width: 1024px)");
    const calm = useMediaQuery("(prefers-reduced-motion: reduce)");
    const finePointer = useMediaQuery("(hover: hover) and (pointer: fine)");
    const walks = wide && !calm;
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            setInView(true);
            return;
        }
        const io = new IntersectionObserver(([entry]) => {
            setInView(entry.isIntersecting && entry.intersectionRatio >= 0.2);
        }, { threshold: [0, 0.2, 0.5], rootMargin: "0px 0px -8% 0px" });
        io.observe(el);
        return () => io.disconnect();
    }, []); 
    useEffect(() => {
        if (!inView) {
            setSettled(false);
            return undefined;
        }
        let t;
        const finish = () => {
            clearTimeout(t);
            t = setTimeout(() => setSettled(true), 80);
        };
        const arm = () => {
            clearTimeout(t);
            t = setTimeout(finish, 320);
        };
        window.addEventListener("scroll", arm, { passive: true });
        if ("onscrollend" in window) {
            window.addEventListener("scrollend", finish);
        }
        arm();
        return () => {
            clearTimeout(t);
            window.removeEventListener("scroll", arm);
            window.removeEventListener("scrollend", finish);
        };
    }, [inView]);
    useEffect(() => {
        if (!walks || !inView || !settled || paused)
            return;
        const id = setInterval(() => setStep((s) => s + 1), STEP_MS);
        return () => clearInterval(id);
    }, [walks, inView, settled, paused]);
    const active = walks && inView && settled ? step % phases.length : -1;
    const cols = phases.map((_, i) => (i === active ? "2fr" : "1fr")).join(" ");
    const hold = (i) => {
        if (!walks || !finePointer)
            return;
        setPaused(true);
        setStep(i);
    };
    return (<div ref={ref} style={walks ? { "--prog-cols": cols } : undefined} onMouseLeave={() => setPaused(false)}>
      <div className={cn("mb-5 hidden gap-5 lg:grid", walks && "prog-track")}>
        {phases.map((p, i) => (<span key={p.label} aria-hidden className={cn("h-1.5 rounded-full transition-colors duration-500", i === active ? "bg-brand-red" : "bg-brand-navy/15")}/>))}
      </div>

      <div className={cn("grid gap-5 md:grid-cols-3", walks && "prog-track")}>
        {phases.map((p, i) => (<article key={p.label} onMouseEnter={() => hold(i)} className={cn("prog-card relative flex min-w-0 flex-col rounded-3xl bg-card p-7 ring-1 md:p-8", i === active
                ? "shadow-xl ring-brand-red/25"
                : "shadow-sm ring-brand-navy/5")}>
            <p className={cn("text-xs font-bold uppercase tracking-[0.18em] transition-colors duration-500", i === active ? "text-brand-red" : "text-brand-navy/40")}>
              {p.phase}
            </p>
            <h3 className="mt-2.5 font-display text-2xl font-semibold text-heading">
              {p.title}
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground md:text-[15px]">
              {p.desc}
            </p>
          </article>))}
      </div>
    </div>);
}
